import { useStore } from 'effector-react';
import { ReactNode, useEffect } from 'react';
import FilterLine from 'components/FilterLine';
import FilterResult from 'components/FilterResult';
import LoadMoreButton from 'components/LoadMoreButton';
import NewGatewayBlock from 'components/NewGatewayBlock';
import NoContent from 'components/NoContent';
import {
	clearPiiFilter,
	piiFilterToArray,
	piiFilterWithGroupsStore,
} from 'components/PiiGlobalFilterV2/model';
import Preloader from 'components/Preloader';
import ResetFilters from 'components/ResetFilters';
import EnhancedTableHead, { TOrder } from 'components/table/EnhancedTableHead';
import GridBody from 'components/table/GridBody';
import GridTable from 'components/table/GridTable';
import { dataCatalogSteps } from 'layouts/AuthorizedWithLeftMenu/Breadcrumbs';
import Header from 'layouts/AuthorizedWithLeftMenu/Header';
import { TAssetsItem } from 'models/assetsExtended/dto';
import { internalAssetsModel } from 'models/assetsExtended/model';
import { PageParamsConfig, usePageParams } from 'services/pageParams';
import { UniversalSearch } from 'views/common/UniversalSearch';
import { SuperAssets } from '../index';
import tableConfig from './config';
import styles from './index.module.css';
import { InternalAssetsTableRow } from './InternalAssetsItem';

const EmptyWrapper = ({ children }: { children: ReactNode }) => <>{children}</>;

const pageConfig = {
	sort: {
		type: 'sort',
		persistence: 'session',
	},
	name: {
		type: 'string',
		persistence: 'session',
	},
	namespaces: {
		type: 'stringArray',
		persistence: 'session',
	},
	label: {
		type: 'string',
		persistence: 'session',
	},
	group: {
		type: 'number',
		persistence: 'session',
	},
	groups: {
		type: 'numberArray',
		persistence: 'session',
	},
	'cluster-ids': {
		type: 'numberArray',
		persistence: 'session',
	},
	'label-keys': {
		type: 'stringArray',
		persistence: 'session',
	},
	'label-values': {
		type: 'stringArray',
		persistence: 'session',
	},
	'ns-label-keys': {
		type: 'stringArray',
		persistence: 'session',
	},
	'ns-label-values': {
		type: 'stringArray',
		persistence: 'session',
	},
	search: {
		type: 'string',
		persistence: 'session',
	},
	'data-types': {
		type: 'numberArray',
		persistence: 'session', // TODO implement 'global' in actuality
	},
	nonempty: {
		type: 'boolean',
		persistence: 'session', // TODO implement 'global' in actuality
	},
} satisfies PageParamsConfig;

type Props = {
	group?: number;
	totalByGroup?: boolean;
};

function InternalAssets(props: Props) {
	const state = useStore(internalAssetsModel.store);
	const piiFilterWithGroups = useStore(piiFilterWithGroupsStore);

	const [pageParams, setPageParams] = usePageParams(
		pageConfig,
		props.group ? 'internalAssetsGroup' : 'internalAssets'
	);

	useEffect(() => {
		setPageParams({
			...pageParams,
			'data-types': piiFilterWithGroups.dataTypes,
			nonempty: piiFilterWithGroups.nonEmpty,
		});
	}, [piiFilterWithGroups]);

	useEffect(() => {
		const {
			name,
			namespaces,
			'label-keys': labelKeys,
			'label-values': labelValues,
			'ns-label-keys': namespaceLabelKeys,
			'ns-label-values': namespaceLabelValues,
			sort,
			group,
			groups,
			'cluster-ids': clusterIds,
			search,
		} = pageParams;

		const dataTypes = piiFilterToArray(piiFilterWithGroups);

		const paramsForFx = {
			name,
			namespaces,
			label_keys: labelKeys,
			label_values: labelValues,
			ns_label_keys: namespaceLabelKeys,
			ns_label_values: namespaceLabelValues,
			is_external: false,
			search,
			'data-types': dataTypes,
			sort: { orderBy: sort.value as keyof TAssetsItem, order: sort.operator },
			group: props.group || group,
			groups,
			cluster_ids: clusterIds,
			total_by_group: !!props.totalByGroup,
		};

		internalAssetsModel.fetchFx(paramsForFx);
	}, [pageParams]);

	// Clean up model on unmount
	useEffect(() => {
		return () => {
			internalAssetsModel.resetFx();
		};
	}, []);

	// That's Table filter, not filters in Filter line.
	function onFilterUpdate(property: keyof TAssetsItem, filterText: string) {
		setPageParams({ ...pageParams, name: filterText });
	}

	function onSortUpdate(property: keyof TAssetsItem) {
		const { sort } = state.params;

		const operator: TOrder = sort.orderBy === property && sort.order === 'asc' ? 'desc' : 'asc';
		const newParams = { sort: { operator, value: property } };

		setPageParams({ ...pageParams, ...newParams });
	}

	function onFilterLineUpdate(filterLineParams: Partial<typeof pageParams>) {
		setPageParams({ ...pageParams, ...filterLineParams });
	}

	function onUniversalSearchUpdate(search: string) {
		setPageParams({ ...pageParams, search });
	}

	function onResetFilters() {
		const newParams = props.group
			? {
					name: '',
					namespaces: [],
					'label-keys': [],
					'label-values': [],
					'ns-label-keys': [],
					'ns-label-values': [],
					search: '',
					'data-types': [],
					'cluster-ids': [],
			  }
			: {
					name: '',
					namespaces: [],
					'label-keys': [],
					'label-values': [],
					'ns-label-keys': [],
					'ns-label-values': [],
					search: '',
					'data-types': [],
					'cluster-ids': [],

					groups: [],
			  };

		setPageParams({ ...pageParams, ...newParams });
		clearPiiFilter();
	}

	// Render

	const { status, data, total, total_filtered, hasMoreData, params } = state;

	const hasFilter =
		params.search.length > 0 ||
		(params['data-types'].length > 0 && params['data-types'][0] !== 'nonempty') ||
		params.name !== '' ||
		params.namespaces.length > 0 ||
		params.label_keys.length > 0 ||
		params.label_values.length > 0 ||
		params.ns_label_keys.length > 0 ||
		params.ns_label_values.length > 0 ||
		params.groups.length > 0 ||
		params.cluster_ids.length > 0;

	const WrapperComponent = !props.group ? SuperAssets : EmptyWrapper;

	return (
		<WrapperComponent>
			{/**
		 This Header should be visible only on the PATHS.SERVICES route
		 and not on the PATHS.ASSET_GROUP_ITEM route
		 */}
			{!props.group && (
				<Header>
					<Header.Breadcrumbs steps={dataCatalogSteps} finalStep="Services" />

					<Header.Title>Service catalog</Header.Title>

					<Header.SensitiveSwitcher />
				</Header>
			)}

			<UniversalSearch value={params.search} onChange={onUniversalSearchUpdate} />

			{props.group ? (
				<FilterLine
					config={[
						'dataTypes',
						'clustersInternal',
						'namespaces',
						'labelKeys',
						'labelValues',
						'namespaceLabelKeys',
						'namespaceLabelValues',
					]}
					values={{
						dataTypes: pageParams['data-types'],
						clustersInternal: pageParams['cluster-ids'],
						namespaces: pageParams.namespaces,
						labelKeys: pageParams['label-keys'],
						labelValues: pageParams['label-values'],
						namespaceLabelKeys: pageParams['ns-label-keys'],
						namespaceLabelValues: pageParams['ns-label-values'],
					}}
					onChange={(newValues) => {
						onFilterLineUpdate({
							'cluster-ids': newValues.clustersInternal,
							namespaces: newValues.namespaces,
							'label-keys': newValues.labelKeys,
							'label-values': newValues.labelValues,
							'ns-label-keys': newValues.namespaceLabelKeys,
							'ns-label-values': newValues.namespaceLabelValues,
						});
					}}
				/>
			) : (
				<FilterLine
					config={[
						'dataTypes',
						'clustersInternal',
						'namespaces',
						'groups',
						'labelKeys',
						'labelValues',
						'namespaceLabelKeys',
						'namespaceLabelValues',
					]}
					values={{
						dataTypes: pageParams['data-types'],
						clustersInternal: pageParams['cluster-ids'],
						namespaces: pageParams.namespaces,
						groups: pageParams.groups,
						labelKeys: pageParams['label-keys'],
						labelValues: pageParams['label-values'],
						namespaceLabelKeys: pageParams['ns-label-keys'],
						namespaceLabelValues: pageParams['ns-label-values'],
					}}
					onChange={(newValues) => {
						onFilterLineUpdate({
							'cluster-ids': newValues.clustersInternal,
							namespaces: newValues.namespaces,
							groups: newValues.groups,
							'label-keys': newValues.labelKeys,
							'label-values': newValues.labelValues,
							'ns-label-keys': newValues.namespaceLabelKeys,
							'ns-label-values': newValues.namespaceLabelValues,
						});
					}}
				/>
			)}

			<FilterResult
				entityLabel="Service"
				loading={status === 'loading'}
				total={total}
				totalFiltered={total_filtered}
			/>

			<GridTable dataTest="internal-assets-table">
				<EnhancedTableHead
					config={props.group ? tableConfig.filter(({ id }) => id !== 'groups') : tableConfig}
					order={params.sort.order}
					orderBy={params.sort.orderBy}
					filterBy={{ name: params.name }}
					onRequestSort={onSortUpdate}
					onRequestFilter={onFilterUpdate}
					rowClassname={props.group ? styles.rowWOGroupsContainer : styles.rowContainer}
				/>

				<Preloader isLoading={status === 'initial' || status === 'loading'}>
					<GridBody data-test="internal-assets-list">
						<>
							{data.length ? (
								data.map((internalAsset) => (
									<InternalAssetsTableRow
										internalAsset={internalAsset}
										oneGroupMode={!!props.group}
										search={params.search}
										key={internalAsset.id}
									/>
								))
							) : hasFilter ? (
								<ResetFilters onReset={onResetFilters} />
							) : (
								<NoContent type="internalAssetData" className={styles.rowContainer}>
									<NewGatewayBlock />
								</NoContent>
							)}

							<LoadMoreButton
								show={hasMoreData}
								loading={status === 'loadingMore'}
								request={internalAssetsModel.fetchMoreFx}
							/>
						</>
					</GridBody>
				</Preloader>
			</GridTable>
		</WrapperComponent>
	);
}

export default InternalAssets;
